// WhatsApp reminders card: mountWaCard(container, { clinicId, lang }) is the ONE entry point the host (the cockpit's Manage view, head or owner only) calls. It is IDEMPOTENT by construction:
// the host rebuilds its whole body on every render() (language switch, _loadAdmins finishing, tab wake), so every call gets a FRESH container and the card's state lives in a module cache keyed by clinicId, never in the DOM.
//   * a mount paints from the cache at once (a skeleton only when nothing is cached) and revalidates in the background;
//   * an in-flight fetch is shared (a burst of re-mounts costs ONE request) and a cache younger than MIN_INTERVAL_MS is not refetched, so a render storm cannot multiply requests;
//   * a result is written only into the LATEST mounted container and only while it is still connected to the document (a stale container from a previous render is never written);
//   * the card never calls the host's render(), adds no listener of its own (dialogs add and remove their own Esc handler) and no timer: refresh happens on mount, which the host does on tab wake, and after each action;
//   * the guide screen, the request dialog's draft and the sending flag live in the cache too, so a re-render during the guide or an open dialog loses nothing.
import { api as defaultApi } from './api.js';
import { STATES, errorKey, timingFromIntegration, timingDirty, timingArgs, chooseTiming, chooseMode, chooseHour } from './state.js';
import { makeT } from './strings.js';
import { ensureStyles } from './styles.js';
import { renderCard, openDialog, h } from './view.js';

export const MIN_INTERVAL_MS = 3000;
const CACHE = new Map();
export const __cache = CACHE;                                  // tests only
export function __reset() { CACHE.clear(); }

function entry(clinicId) {
  let e = CACHE.get(clinicId);
  if (!e) { e = { clinicId, load: null, at: 0, inflight: null, mount: null, ui: { screen: 'main', draft: { pref: 'phone', when: '' }, sending: false, cancelling: false, timing: null, busy: { timing: false, shabbat: false, switch: false, disc: false } }, dialogs: 0 }; CACHE.set(clinicId, e); }
  return e;
}
const hostOf = (opts) => opts.host || (typeof window !== 'undefined' ? window.HUD : null);

function paint(e) {
  const m = e.mount; if (!m || !m.container || !m.container.isConnected) return;     // a container from an earlier render is dropped silently
  const doc = m.container.ownerDocument;
  const t = makeT(m.host);
  const ctx = {
    doc, t, lang: m.lang, ui: e.ui, load: e.load, resolved: e.load ? e.load.resolved : null, act: e.load ? e.load.act : null, integ: e.load ? e.load.integ : null,
    on: { activate: () => { e.ui.screen = 'guide'; paint(e); }, back: () => { e.ui.screen = 'main'; paint(e); }, ask: () => askDialog(e), cancel: () => cancelDialog(e), retry: () => revalidate(e, true),
      chooseTiming: v => { e.ui.timing = chooseTiming(curTiming(e), v); paint(e); }, chooseMode: v => { e.ui.timing = chooseMode(curTiming(e), v, !!(e.load && e.load.integ && e.load.integ.reminder_mode === 'manual')); paint(e); }, chooseHour: v => { e.ui.timing = chooseHour(curTiming(e), v); paint(e); },
      saveTiming: () => saveTimingDialog(e), toggleShabbat: () => toggleShabbat(e), toggleSwitch: () => toggleSwitch(e), disconnect: () => disconnectDialog(e) }
  };
  const integ = ctx.integ;
  ctx.busy = e.ui.busy;
  ctx.timing = integ ? (e.ui.timing || timingFromIntegration(integ)) : null;
  ctx.timingDirty = !!(integ && ctx.timing && timingDirty(ctx.timing, integ));
  const focusId = doc.activeElement && doc.activeElement.getAttribute ? doc.activeElement.getAttribute('data-wac-focus') : null;     // a repaint must not drop the keyboard focus
  const node = renderCard(ctx);
  if (node) { m.container.replaceChildren(node); m.container.hidden = false; if (focusId) { const again = m.container.querySelector('[data-wac-focus="' + focusId + '"]'); if (again && !again.disabled && again.focus) again.focus(); } }
  else { m.container.replaceChildren(); m.container.hidden = true; }                 // nothing to show: the packages are not applied yet, or this state has no card
}

export function revalidate(e, force) {
  if (e.inflight) return e.inflight;                                                  // dedupe: one request chain per clinic at a time
  if (!force && e.load && Date.now() - e.at < MIN_INTERVAL_MS) return Promise.resolve();
  const m = e.mount; const apiObj = (m && m.api) || defaultApi;
  e.inflight = apiObj.load(e.clinicId).then(res => { e.load = res; e.at = Date.now(); }, err => { e.load = { unavailable: false, error: { code: 'NETWORK', message: String(err && err.message || err) }, act: null, integ: null, resolved: null }; e.at = Date.now(); })
    .then(() => { e.inflight = null; if (e.load && e.load.resolved && e.load.resolved.state !== STATES.NOT_CONNECTED) e.ui.screen = 'main'; paint(e); });
  return e.inflight;
}

export function mountWaCard(container, opts) {
  const o = opts || {};
  if (!container || !o.clinicId) return;
  const host = hostOf(o);
  const e = entry(String(o.clinicId));
  e.mount = { container, lang: o.lang === 'he' ? 'he' : 'en', host, api: o.api || null };
  ensureStyles(container.ownerDocument);
  paint(e);                                                                           // from the cache now (or the skeleton)
  revalidate(e, false);
}

// ---- actions ----
const curTiming = (e) => e.ui.timing || timingFromIntegration(e.load && e.load.integ);
// run one write: marks the control busy and repaints, runs fn, ALWAYS clears busy; the result is reported by the caller
async function guarded(e, flag, fn) {
  if (e.ui.busy[flag]) return null; e.ui.busy[flag] = true; paint(e);
  try { return await fn(); } finally { e.ui.busy[flag] = false; }
}
async function toggleShabbat(e) {
  const integ = e.load && e.load.integ; if (!integ || (e.load.resolved && e.load.resolved.paused)) return;
  const apiObj = (e.mount && e.mount.api) || defaultApi, t = tNow(e); const next = integ.shabbat_skip === false;
  const r = await guarded(e, 'shabbat', () => apiObj.saveShabbat(e.clinicId, next));
  if (!r) return;
  if (r.ok) toast(e, t('wac_toast_saved'), 'success'); else toast(e, t(errorKey('shabbat', r.error)), 'error');
  await revalidate(e, true);
}
async function toggleSwitch(e) {
  const resolved = e.load && e.load.resolved; if (!resolved || resolved.state !== STATES.CONNECTED) return;
  if (resolved.paused !== true) { pauseDialog(e); return; }                         // pausing permanently skips the reminders already waiting: one deliberate tap (CTO ruling); resuming needs none
  return switchWrite(e, true);
}
async function switchWrite(e, resume) {
  const apiObj = (e.mount && e.mount.api) || defaultApi, t = tNow(e);
  const r = await guarded(e, 'switch', () => apiObj.setEnabled(e.clinicId, resume));
  if (!r) return;
  if (r.ok) toast(e, t(resume ? 'wac_toast_resumed' : 'wac_toast_paused'), 'success'); else toast(e, t(errorKey('switch', r.error)), 'error');
  await revalidate(e, true);
}
function pauseDialog(e) {
  if (e.dialogs > 0) return;
  const doc = docOf(e), t = tNow(e);
  e.dialogs++;
  openDialog(doc, {
    title: t('wac_pause_confirm_title'), labelId: 'wac-dlg-pause', onClose: () => { e.dialogs = Math.max(0, e.dialogs - 1); },
    build: (bd) => { bd.appendChild(h(doc, 'p', { class: 'wac-p', text: t('wac_pause_note') })); return {}; },
    actions: [
      { id: 'cancel', label: t('btn_cancel'), cls: 'ghost', onClick: (close) => close() },
      { id: 'pause', label: t('wac_pause_confirm_btn'), cls: 'primary', onClick: (close, b) => { b.disabled = true; close(); switchWrite(e, false); } }
    ]
  });
}
function saveTimingDialog(e) {
  if (e.dialogs > 0) return; if (e.load && e.load.resolved && e.load.resolved.paused) return;      // defensive: the button is already disabled while paused (qa-3, CTO 2026-11-02)
  const model = curTiming(e); if (!model) return;
  const doc = docOf(e), t = tNow(e); const apiObj = (e.mount && e.mount.api) || defaultApi;
  e.dialogs++;
  openDialog(doc, {
    title: t('wac_save_title'), labelId: 'wac-dlg-save', onClose: () => { e.dialogs = Math.max(0, e.dialogs - 1); },
    build: (bd) => { bd.appendChild(h(doc, 'p', { class: 'wac-p', text: t('wac_save_body') })); return {}; },
    actions: [
      { id: 'cancel', label: t('btn_cancel'), cls: 'ghost', onClick: (close) => close() },
      { id: 'ok', label: t('btn_save'), cls: 'primary', onClick: async (close, b) => {
        b.disabled = true; close();                                                       // a second click cannot double-send: guarded() refuses while the write is in flight
        const r = await guarded(e, 'timing', () => apiObj.saveTiming(e.clinicId, timingArgs(model)));
        if (!r) return;
        if (r.ok) { e.ui.timing = null; toast(e, t('wac_toast_saved'), 'success'); } else toast(e, t(errorKey('timing', r.error)), 'error');      // the draft stays after a refusal so the head can correct it
        await revalidate(e, true);
      } }
    ]
  });
}
function disconnectDialog(e) {
  if (e.dialogs > 0) return;
  const doc = docOf(e), t = tNow(e); const apiObj = (e.mount && e.mount.api) || defaultApi;
  e.dialogs++;
  openDialog(doc, {
    title: t('wac_disc_title'), labelId: 'wac-dlg-disc', onClose: () => { e.dialogs = Math.max(0, e.dialogs - 1); },
    build: (bd) => { bd.appendChild(h(doc, 'p', { class: 'wac-p', text: t('wac_disc_body') })); return {}; },
    actions: [
      { id: 'keep', label: t('wac_keep'), cls: 'ghost', onClick: (close) => close() },
      { id: 'confirm', label: t('wac_disc_ok'), cls: 'danger', onClick: async (close, b) => {
        b.disabled = true; close();
        const r = await guarded(e, 'disc', () => apiObj.disconnect(e.clinicId));
        if (!r) return;
        if (r.ok) { e.ui.timing = null; toast(e, t('wac_toast_disc'), 'success'); } else toast(e, t(errorKey('disconnect', r.error)), 'error');
        await revalidate(e, true);
      } }
    ]
  });
}
const toast = (e, msg, type) => { const m = e.mount; try { if (m && m.host && typeof m.host.toast === 'function') m.host.toast(msg, type); } catch (_) {} };
const tNow = (e) => makeT(e.mount && e.mount.host);
const docOf = (e) => (e.mount && e.mount.container && e.mount.container.ownerDocument) || document;

function askDialog(e) {
  if (e.dialogs > 0) return;
  const doc = docOf(e), t = tNow(e); const apiObj = (e.mount && e.mount.api) || defaultApi; const d = e.ui.draft;
  let errLine, sendBtn;
  e.dialogs++;
  openDialog(doc, {
    title: t('wac_ask'), labelId: 'wac-dlg-ask', onClose: () => { e.dialogs = Math.max(0, e.dialogs - 1); },
    build: (bd) => {
      bd.appendChild(h(doc, 'p', { class: 'wac-p', text: t('wac_req_body') }));
      const sel = h(doc, 'select', { id: 'wac-pref', 'aria-label': t('wac_req_pref') }, [['phone', t('wac_pref_phone')], ['whatsapp', t('wac_pref_whatsapp')], ['email', t('settings_email')]].map(([v, l]) => h(doc, 'option', { value: v, text: l, selected: d.pref === v })));
      sel.value = d.pref; sel.onchange = () => { d.pref = sel.value; };
      const inp = h(doc, 'input', { id: 'wac-when', type: 'text', maxlength: '80', placeholder: t('wac_req_when_ph'), autocomplete: 'off' }); inp.value = d.when; inp.oninput = () => { d.when = inp.value; };
      bd.appendChild(h(doc, 'div', { class: 'wac-field' }, [h(doc, 'label', { for: 'wac-pref', text: t('wac_req_pref') }), sel]));
      bd.appendChild(h(doc, 'div', { class: 'wac-field' }, [h(doc, 'label', { for: 'wac-when', text: t('wac_req_when') }), inp]));
      errLine = h(doc, 'div', { class: 'wac-err', role: 'alert', hidden: true }); bd.appendChild(errLine);
      return { sel, inp };
    },
    actions: [
      { id: 'cancel', label: t('btn_cancel'), cls: 'ghost', onClick: (close) => close() },
      { id: 'send', label: t('wac_req_send'), cls: 'primary', onClick: async (close, b) => {
        if (e.ui.sending) return; e.ui.sending = true; b.disabled = true; errLine.hidden = true;
        let r; try { r = await apiObj.requestActivation(e.clinicId, d.pref, d.when.trim()); } finally { e.ui.sending = false; }
        if (r.ok) { d.when = ''; close(); toast(e, t('wac_toast_req_sent'), 'success'); e.ui.screen = 'main'; revalidate(e, true); return; }
        b.disabled = false; errLine.textContent = t(errorKey('request', r.error)); errLine.hidden = false;
        if (r.error && (r.error.hint === 'request_open' || r.error.hint === 'integration_exists')) revalidate(e, true);        // the card is out of date: refresh it behind the dialog
      } }
    ]
  });
}

function cancelDialog(e) {
  if (e.dialogs > 0) return;
  const doc = docOf(e), t = tNow(e); const apiObj = (e.mount && e.mount.api) || defaultApi;
  e.dialogs++;
  openDialog(doc, {
    title: t('wac_cancel_title'), labelId: 'wac-dlg-cancel', onClose: () => { e.dialogs = Math.max(0, e.dialogs - 1); },
    build: (bd) => { bd.appendChild(h(doc, 'p', { class: 'wac-p', text: t('wac_cancel_body') })); return {}; },
    actions: [
      { id: 'keep', label: t('wac_keep'), cls: 'ghost', onClick: (close) => close() },
      { id: 'confirm', label: t('wac_cancel_req'), cls: 'danger', onClick: async (close, b) => {
        if (e.ui.cancelling) return; e.ui.cancelling = true; b.disabled = true;
        let r; try { r = await apiObj.cancelActivation(e.clinicId); } finally { e.ui.cancelling = false; }
        close();
        if (r.ok) toast(e, t('wac_toast_req_cancelled'), 'success'); else toast(e, t(errorKey('cancel', r.error)), 'error');
        revalidate(e, true);
      } }
    ]
  });
}
